/**
 * Cross-module X XWP-13 (refonte WP-38; design/ux/04_produits_stock_assistant.md §1.1-§1.3; X
 * 02_DESIGN §3.4-§3.5) — every French string of the Produits list, the product panel and its
 * dialogs (Génériques, Comparer les prix d'achat, Bloquer une quantité, Lever le blocage). Never a
 * literal in a component (one place to review the copy).
 */
import { fmtInt } from "./locale.config.js";

const pluriel = (n, un, plusieurs) => (Number(n) > 1 ? plusieurs : un);

export const PRODUITS_LISTE = Object.freeze({
  titre: "Liste des produits",
  grille: "Produits",
  colonnes: Object.freeze({
    nom: "Nom commercial",
    dci: "DCI",
    laboratoire: "Laboratoire / Marque",
    forme: "Forme · Dosage",
    enr: "N° ENR",
  }),
  marque: (m) => `${m} (marque)`,
  badgeRupture: "Rupture",
  badgeBloque: "Bloqué",
  recherche: "Rechercher : nom, DCI, labo, N° ENR, code-barres…",
  rechercheLabel: "Rechercher un produit",
  /** « 4 022 produits · 38 en rupture · 2 bloqués » (counts of the active filters). */
  sousTitre: ({ total, en_rupture, bloques }, filtre) =>
    [
      `${fmtInt(total)} ${pluriel(total, "produit", "produits")}${filtre ? " (filtrés)" : ""}`,
      `${fmtInt(en_rupture)} en rupture`,
      `${fmtInt(bloques)} ${pluriel(bloques, "bloqué", "bloqués")}`,
    ].join(" · "),
  sousTitreChargement: "…",
  importer: "Importer",
  importerNomenclature: "Ajouter depuis la nomenclature",
  importerTout: "Tout importer depuis la nomenclature",
  importerChifa: "Importer CHIFA",
  exporter: "Exporter",
  exporterCsv: "Exporter CSV",
  gererTags: "Gérer les tags",
  nouveau: "Nouveau produit",
  filtres: Object.freeze({
    type: "Type",
    laboratoire: "Laboratoire / Marque",
    etat: "État",
    liste: "Liste",
    remboursable: "Remboursable",
    psychotrope: "Psychotrope",
    tags: "Tags",
    fournisseur: "Fournisseur habituel",
    plus: "Plus de filtres",
  }),
  etats: Object.freeze({ EN_STOCK: "En stock", RUPTURE: "Rupture", BLOQUE: "Bloqué" }),
  oui: "Oui",
  non: "Non",
  vide: (recherche, filtre) =>
    recherche
      ? `Aucun produit ne correspond à « ${recherche} »${filtre ? " avec les filtres actifs" : ""}.`
      : filtre
        ? "Aucun produit ne correspond aux filtres actifs."
        : "Le catalogue est vide.",
  videAction: "Créer le produit",
  erreur: "Impossible de charger les produits.",
  pagination: (debut, fin, total) => `${fmtInt(debut)}–${fmtInt(fin)} sur ${fmtInt(total)}`,
  pagePrecedente: "Précédent",
  pageSuivante: "Suivant",
  page: (p, n) => `Page ${fmtInt(p)} / ${fmtInt(n)}`,
  aideRaccourcis: "Aide raccourcis : F1",
});

export const PANNEAU_PRODUIT = Object.freeze({
  fermer: "Fermer (Échap)",
  chargement: "Chargement du produit…",
  erreur: "Impossible de charger les lots",
  reessayer: "Réessayer",
  introuvable: "Ce produit n'existe plus ou n'est pas visible.",
  correctionFaite: (delta, numero) =>
    `Stock corrigé : ${Number(delta) >= 0 ? "+" : "−"}${fmtInt(Math.abs(Number(delta) || 0))} sur le lot ${numero ?? "non renseigné"}`,
  correctionErreur: "La correction du stock a échoué.",
  aucunLot: "Aucun lot pour ce produit. Il sera créé à la réception d'une facture.",
  chips: Object.freeze({
    remboursable: (tr) => (tr ? `Remboursable · T.R ${tr}` : "Remboursable"),
    nonRemboursable: "Non remboursable",
    psychotrope: (ordonnance) => (ordonnance ? `Psychotrope · ${ordonnance}` : "Psychotrope"),
    locale: "Origine : Locale",
    importee: "Origine : Importée",
    actif: "Actif",
    bloque: "Bloqué",
    venteLibreBloquee: "Vente libre bloquée",
  }),
  resume: Object.freeze({
    vendable: "Stock vendable",
    perime: "Périmé",
    lots: "Lots",
    qteMin: "QTE MIN",
    seuil: "Seuil d'alerte",
    enCommande: "En commande",
    codes: "Codes-barres",
    bloque: "Bloqué",
    reserve: "Réservé en caisse",
    nonEvalue: "Non évalué",
  }),
  periode: Object.freeze({
    label: "Période",
    preset: (j) => `${j} j`,
    personnalisee: "Personnalisée…",
    du: "Du",
    au: "Au",
    appliquer: "Appliquer",
    resume: (jours, vendues, parJour, couverture) =>
      `Sur ${fmtInt(jours)} j : ${fmtInt(vendues)} ${pluriel(vendues, "vendue", "vendues")} · ${parJour} /j · ${
        couverture == null ? "Pas de vente" : `couvre ${fmtInt(couverture)} j`
      }`,
  }),
  groupes: Object.freeze({
    vendables: (n) => `Lots vendables (${fmtInt(n)})`,
    bloques: (n) => `Lots bloqués (${fmtInt(n)})`,
    perimes: (n) => `Lots périmés (${fmtInt(n)})`,
    epuises: (n) => `Lots épuisés (${fmtInt(n)})`,
    epuisesTronques: (affiches, total) => `${fmtInt(affiches)} plus récents sur ${fmtInt(total)}`,
    lotsTronques: "Seuls les lots les plus proches de la péremption sont affichés.",
    ordre: "Ordre : FEFO",
  }),
  actions: Object.freeze({
    modifier: "Modifier la fiche",
    generiques: "Génériques",
    etiquettes: "Imprimer étiquettes",
    comparer: "Comparer les prix d'achat",
    bloquer: "Bloquer une quantité",
    plus: "Plus d'actions",
    bloquerVente: "Bloquer / débloquer la vente",
    voirStock: "Voir dans Stock & lots",
    voirMagasins: "Voir dans Magasins",
    precedent: "Produit précédent",
    suivant: "Produit suivant",
  }),
});

export const CARTE_LOT = Object.freeze({
  lot: "Lot",
  lotNonRenseigne: "Lot non renseigné",
  codeLot: "Code lot",
  code128: "Code 128",
  peremption: "Péremption",
  sansPeremption: "Sans péremption",
  dans: (j) => `dans ${fmtInt(j)} j`,
  aujourdhui: "périme aujourd'hui",
  perimeDepuis: (j) => `Périmé depuis ${fmtInt(j)} j`,
  magasin: "Magasin",
  reste: "Reste",
  resteDetail: (payees, ug) => `${fmtInt(payees)} payées + ${fmtInt(ug)} UG`,
  resteCourt: (reste, payees, ug) => `Reste ${fmtInt(reste)} (${fmtInt(payees)} + ${fmtInt(ug)} UG)`,
  recu: "Reçu",
  recuDetail: (payees, ug) => `${fmtInt(payees)} reçues + ${fmtInt(ug)} UG`,
  qtePayee: (q) => `Qté payée ${fmtInt(q)}`,
  /** « Reste 100 · Bloqué 30 · Réservé en caisse 5 · Vendable 65 » (02_DESIGN §3.4). */
  couches: ({ reste, bloque, reserve, vendable }) =>
    `Reste ${fmtInt(reste)} · Bloqué ${fmtInt(bloque)} · Réservé en caisse ${fmtInt(reserve)} · Vendable ${fmtInt(vendable)}`,
  disponibles: (n) => `${fmtInt(n)} ${pluriel(n, "disponible", "disponibles")}`,
  nonDisponible: "Non disponible",
  puHt: "PU HT",
  ppa: "PPA",
  tr: "T.R",
  marge: "Marge",
  margeFormule: "(PPA − PU HT) ÷ PU HT",
  margeUnitaire: "Marge unitaire",
  comptoir: "comptoir",
  chifaTheorique: "CHIFA (théorique)",
  ug: "UG",
  vendues: (jours) => `Vendues ${fmtInt(jours)} j`,
  rotation: "Rotation",
  parJour: (v) => `${v} /j`,
  blocages: (n) => `Blocages (${fmtInt(n)})`,
  blocageLigne: (q, motif) => `${fmtInt(q)} ${pluriel(q, "unité", "unités")} · ${motif}`,
  blocagePar: (nom, date) => `par ${nom ?? "—"} le ${date}`,
  lever: "Lever",
  bloquer: "Bloquer une quantité",
  etiquettes: "Imprimer étiquettes",
  corriger: "Corriger le stock",
  repartition: "Répartir entre les magasins",
  voirDocument: "Voir le document",
  developper: "Afficher le détail du lot",
  reduire: "Masquer le détail du lot",
  // X XWP-97 (D-49, XO-23 (5)) — « Invisible aux caissiers », beside « Bloquer une quantité » (P only)
  invisible: "Invisible aux caissiers",
  invisibleBadge: "Invisible",
  invisiblePartiel: (n, total) => `Invisible (${fmtInt(n)}/${fmtInt(total)})`,
  invisibleAide: "Le lot n'est ni proposé ni vendable au comptoir ; propriétaire et manager le voient toujours.",
  invisiblePortee: (libelle) => `Portée : ${libelle}`,
  invisiblePar: (nom, date) => `Rendu invisible par ${nom ?? "—"} le ${date}`,
  invisibleToutes: (n) => `Invisible aux caissiers (les ${fmtInt(n)} réceptions)`,
  visibiliteMasquee: (numero) => `Lot ${numero} invisible aux caissiers.`,
  visibiliteRendue: (numero) => `Lot ${numero} de nouveau visible aux caissiers.`,
  visibiliteErreur: "La visibilité du lot n'a pas pu être modifiée.",
  confirmerToutesTitre: (invisible) => (invisible ? "Rendre toutes les réceptions invisibles ?" : "Rendre toutes les réceptions visibles ?"),
  confirmerToutes: (n, numero, invisible) =>
    invisible
      ? `Les ${fmtInt(n)} réceptions du lot ${numero} ne seront plus proposées ni vendables au comptoir.`
      : `Les ${fmtInt(n)} réceptions du lot ${numero} seront de nouveau proposées au comptoir.`,
  confirmerToutesOui: (invisible) => (invisible ? "Rendre invisibles" : "Rendre visibles"),
  // X XWP-97 (XO-25) — identical receptions (same N° de lot, péremption, PPA) in one card
  receptions: (n) => `${fmtInt(n)} ${pluriel(n, "réception", "réceptions")}`,
  receptionsTitre: (n) => `Réceptions (${fmtInt(n)}) — la vente prend la plus ancienne d'abord`,
  receptionRecue: (date) => `Reçue le ${date}`,
  receptionQuantites: (reste, recu) => `Reste ${fmtInt(reste)} sur ${fmtInt(recu)} reçues`,
  receptionBloque: (n) => `Bloqué ${fmtInt(n)}`,
  puHtPlage: "selon la réception",
});

export const GENERIQUES = Object.freeze({
  titre: ({ dci, forme, dosage }) => `Génériques — ${[dci, forme, dosage].filter(Boolean).join(" · ")}`,
  correspondance: "Même DCI × Forme × Dosage (A-12)",
  inclureNomenclature: "Inclure la nomenclature",
  groupe: (stock, parJour, couverture) =>
    `Groupe : stock ${fmtInt(stock)} · ${parJour} /j · ${couverture == null ? "Pas de vente" : `couverture ${fmtInt(couverture)} j`}`,
  colonnes: Object.freeze({
    produit: "Produit",
    pg: "P/G",
    stock: "Stock",
    ppa: "PPA",
    puHt: "PU HT",
    marge: "Marge",
    vendues: "Vendues",
    rotation: "Rot./j",
    couverture: "Couv.",
    enCommande: "En cde",
  }),
  ceProduit: "(ce produit)",
  lots: (n) => `${fmtInt(n)} l.`,
  puDernier: "dernier prix connu",
  pasDeVente: "Pas de vente",
  nonReference: "Non référencé",
  ajouter: "Ajouter au catalogue",
  ajoute: (nom) => `${nom} ajouté au catalogue`,
  dejaPresent: "Ce produit est déjà au catalogue.",
  aucun: "Aucun générique au catalogue de la pharmacie.",
  sansCle: "La DCI, la forme ou le dosage de ce produit n'est pas renseigné : aucune équivalence ne peut être établie.",
  nomenclatureVide: "Aucun autre produit de la nomenclature ne correspond.",
  enCommandeNonEvalue: "En commande : non évalué — bons de commande pharmacie à venir",
  legende: "P = princeps · G = générique. Entrée : ouvrir · Ctrl+P : imprimer · Échap : fermer",
  footnote: "Correspondance : DCI + forme + dosage normalisés",
  erreur: "Impossible de charger les génériques.",
});

export const COMPARER_PRIX = Object.freeze({
  titre: (nom) => `Comparer les prix d'achat — ${nom}`,
  sousTitre: (mois) => `Chaque barre est un achat (24 mois au plus) : facture validée ou offre reçue. Fenêtre : ${fmtInt(mois)} mois.`,
  afficherGeneriques: "Afficher les génériques",
  afficherTableau: "Afficher le tableau",
  masquerTableau: "Masquer le tableau",
  axeY: "Prix d'achat HT (DA)",
  axeX: "Achats (ordre chronologique)",
  aucun: "Aucun achat enregistré pour ce produit sur la période.",
  tronque: (max) => `Seuls les ${fmtInt(max)} achats les plus récents sont affichés.`,
  infobulle: ({ date, quantite, ug, fournisseur, prix }) =>
    `${date} · Quantité ${fmtInt(quantite)}${ug ? ` (+${fmtInt(ug)} UG)` : ""} · ${fournisseur ?? "Fournisseur non renseigné"} · ${prix}`,
  colonnes: Object.freeze({ date: "Date", produit: "Produit", quantite: "Quantité", ug: "UG", fournisseur: "Fournisseur", prix: "PU HT (DA)", piece: "Pièce" }),
  navigation: "← / → : achat précédent / suivant",
  erreur: "Impossible de charger l'historique des prix d'achat.",
});

export const BLOCAGE_LOT = Object.freeze({
  titre: "Bloquer une quantité",
  lot: "Lot",
  lotOption: (numero, peremption, libre, reception = null) =>
    `${numero} · ${peremption} · ${fmtInt(libre)} bloquables${reception ? ` · ${reception}` : ""}`,
  figures: ({ vendable, bloque, reserve }) =>
    `${fmtInt(vendable)} vendables · ${fmtInt(bloque)} déjà ${pluriel(bloque, "bloquée", "bloquées")} · ${fmtInt(reserve)} ${pluriel(reserve, "réservée", "réservées")} en caisse`,
  maximum: (n) => `Maximum bloquable : ${fmtInt(n)}`,
  quantite: "Quantité à bloquer",
  motif: "Motif",
  precision: "Précision",
  precisionRequise: "Précisez le motif du blocage (motif « Autre »).",
  quantiteInvalide: "Saisissez une quantité entière supérieure à 0.",
  quantiteTrop: (n) => `Au plus ${fmtInt(n)} unités peuvent être bloquées sur ce lot.`,
  confirmerTitre: "Confirmer le blocage",
  effet: (q, numero) => `${fmtInt(q)} ${pluriel(q, "unité", "unités")} du lot ${numero} ne ${pluriel(q, "sera", "seront")} plus ${pluriel(q, "vendable", "vendables")} en caisse.`,
  valider: "Bloquer",
  succes: (q, numero) => `${fmtInt(q)} ${pluriel(q, "unité bloquée", "unités bloquées")} sur le lot ${numero}`,
  conflit: (n) => `Le stock a changé : ${fmtInt(n)} ${pluriel(n, "unité est", "unités sont")} encore bloquable${n > 1 ? "s" : ""}.`,
  aucunLot: "Aucun lot vendable à bloquer.",
  chargement: "Lecture des quantités du lot…",
  leverTitre: "Lever le blocage",
  leverMessage: (q, motif, numero) => `${fmtInt(q)} ${pluriel(q, "unité", "unités")} (${motif}) du lot ${numero} ${pluriel(q, "redeviendra", "redeviendront")} vendable${q > 1 ? "s" : ""}.`,
  motifLevee: "Motif de la levée",
  motifLeveeRequis: "Indiquez pourquoi le blocage est levé.",
  lever: "Lever le blocage",
  leve: "Blocage levé",
});

/**
 * Refonte WP-109 (CP §15.7) — the « Importer un produit depuis CHIFA » dialog of the Produits list:
 * look-up of a N° CNAS in the Base CNAS, then « Importer ce produit » opens the prefilled form (or the
 * fiche of the product already in the catalogue).
 */
export const IMPORT_CNAS_DIALOGUE = Object.freeze({
  titre: "Importer un produit depuis CHIFA",
  sousTitre: "Recherchez par N° d'enregistrement CNAS : le produit s'ouvre prérempli, vous complétez puis enregistrez.",
  numero: "N° d'enregistrement CNAS",
  numeroAide: (min) => `${min} caractères au moins. Entrée : importer ce produit.`,
  numeroExemple: "ex. 01355",
  recherche: "Recherche dans la Base CNAS…",
  trouve: "Trouvé",
  dejaAuCatalogue: "Déjà au catalogue",
  dejaAuCatalogueTexte: (nom) => `Ce numéro est déjà le produit « ${nom} » de votre catalogue.`,
  passerelle: "Lu par la passerelle CHIFA",
  forme: "Forme",
  dosage: "Dosage",
  conditionnement: "Conditionnement",
  ppa: "PPA (plafond CNAS)",
  tarifRef: "Tarif de référence",
  numeroEnr: "N° enregistrement",
  remboursable: "Remboursable",
  aCompleter: (n) => (n > 1 ? `${n} champs seront à compléter dans la fiche.` : "Un champ sera à compléter dans la fiche."),
  introuvable: "Aucun produit trouvé avec ce N° dans la Base CNAS. Vérifiez le numéro ou créez le produit vous-même.",
  echec: "La Base CNAS n'a pas pu être lue. Vérifiez la connexion puis réessayez.",
  annuler: "Annuler",
  importer: "Importer ce produit",
  ouvrirFiche: "Ouvrir la fiche",
});

/**
 * X XWP-14 (refonte WP-39; ux/04 §1.4) — the fiche produit (/produits/:id, /produits/nouveau).
 * Field labels shared with the S2 creation panel come from config/achat.messages.js S2 (one
 * configuration, one wording); this block holds only what the fiche adds.
 */
export const FICHE_PRODUIT = Object.freeze({
  titreModifier: (nom) => `${nom} — Modifier la fiche`,
  titreNouveau: "Nouveau produit",
  titreChargement: "Fiche produit",
  sousTitreNouveau: "Création d'un produit dans le catalogue de la pharmacie",
  issuNomenclature: (num) => `Issu de la nomenclature · N° ${num}`,
  sections: "Sections",
  sectionsAide: "Alt+Pg préc. / Alt+Pg suiv.",
  type: "Type",
  // ── états de la fiche
  chargement: "Chargement de la fiche…",
  echecChargementTitre: "La fiche n'a pas pu être chargée",
  echecChargement:
    "Rien n'est affiché pour ne pas risquer d'écraser le produit. Vérifiez la connexion puis réessayez.",
  introuvable: "Ce produit est introuvable (supprimé, ou d'une autre pharmacie).",
  reessayer: "Réessayer",
  retourListe: "Retour à la liste",
  nonEnregistre: "Non enregistré",
  aJour: "Aucune modification",
  enregistrement: "Enregistrement…",
  lectureSeule: "Lecture seule — votre rôle ne permet pas de modifier les produits.",
  // ── identité
  paysOrigine: "Pays d'origine",
  // refonte WP-101 (D-42) — the producer the type does not carry, kept read-only as history
  laboratoireHistorique: "Laboratoire (historique)",
  laboratoireHistoriqueAide: (type) => `Conservé pour l'historique, non modifiable : un produit « ${type} » est identifié par sa marque.`,
  marqueHistorique: "Marque (historique)",
  marqueHistoriqueAide: (type) => `Conservée pour l'historique, non modifiable : un produit « ${type} » est rattaché à un laboratoire.`,
  rechercheNomenclature: "Tapez 2 caractères ou plus : la nomenclature nationale propose le médicament.",
  nomenclatureVide: "Aucun médicament de la nomenclature ne correspond.",
  conflitsTitre: "La nomenclature propose d'autres valeurs",
  conflitsAide: "Les champs vides ont été remplis. Pour ceux que vous aviez déjà saisis, choisissez.",
  conflitLigne: (champ, nomenclature, actuel) => `${champ} : nomenclature « ${nomenclature} » · vous « ${actuel} »`,
  garderLeMien: "Garder le mien",
  prendreNomenclature: "Prendre la nomenclature",
  // ── code-barre de la boîte (ux/04 §1.4.3)
  codesTitre: "Code-barre de la boîte",
  codesSousTitre: "(ce n'est pas le code du lot)",
  scannez: "Scannez la boîte",
  ajouterCode: "Ajouter",
  aucunCode: "Aucun code-barre. Scannez la boîte : le code s'ajoute ici, où que soit le curseur.",
  principal: "Principal",
  rendrePrincipal: (code) => `Faire de ${code} le code principal`,
  rendrePrincipalCourt: "Rendre principal",
  retirer: "Retirer",
  retirerCode: (code) => `Retirer le code ${code}`,
  ouvrirProduit: "Ouvrir ce produit",
  continuerQuandMeme: "Continuer quand même",
  codeAjoute: (code) => `Code-barre ${code} ajouté.`,
  codeDejaPresent: (code) => `Le code ${code} figure déjà sur cette fiche.`,
  // ── CNAS / CHIFA
  numEnrAide: "Requis si le médicament est remboursable. La base CNAS est vérifiée automatiquement.",
  prefillCnas: "Préremplir depuis la base CNAS",
  prefillCnasAide: "Remplit les champs vides depuis la base CNAS ; vous vérifiez puis enregistrez.",
  prefillCnasFait: "Valeurs de la base CNAS reprises dans les champs vides — vérifiez puis enregistrez.",
  prefillCnasIntrouvable: "Ce N° d'enregistrement est introuvable dans la base CNAS.",
  // ── « Importer ce produit » depuis la Base CNAS (refonte WP-109, CP §15.7)
  importCnasChargement: "Lecture de la Base CNAS…",
  importCnasPrerempli: (num) => `Prérempli depuis la Base CNAS — N° ${num}. Vérifiez chaque valeur, complétez puis enregistrez (Ctrl+S).`,
  importCnasPasserelle: "Le numéro a été lu par la passerelle CHIFA (il n'est pas dans la copie locale de la Base CNAS).",
  importCnasNomenclature: (num, champs) => `Complété par la nomenclature nationale (N° ${num}) : ${champs}.`,
  importCnasACompleter: "À compléter ou à vérifier — la Base CNAS ne les donne pas :",
  importCnasRienACompleter: "La Base CNAS donne tous les champs repris ; vérifiez-les avant d'enregistrer.",
  importCnasComplete: "Les champs signalés sont renseignés ; vérifiez puis enregistrez (Ctrl+S).",
  importCnasIntrouvable: (num) => `Le N° ${num} est introuvable dans la Base CNAS : saisissez le produit vous-même.`,
  importCnasDejaAuCatalogue: (nom) => `« ${nom} » est déjà au catalogue : sa fiche est ouverte.`,
  aCompleterChamp: "À compléter : la Base CNAS ne donne pas cette valeur.",
  cnasVerification: "Vérification CNAS…",
  cnasConfirme: (taux) => `Remboursable CNAS confirmé${taux ? ` · taux ${taux} %` : ""}`,
  cnasNonRemboursable: "Trouvé dans la base CNAS — non remboursable",
  cnasIntrouvable: "Absent de la base CNAS — saisie manuelle",
  cnasTr: (tr) => `T.R publié par la CNAS : ${tr}`,
  trAide: "Le tarif tel que publié par la CNAS (la majoration de production locale s'applique à la vente).",
  origineAide: "Production locale : majoration CHIFA à la vente (A-10).",
  typeOrdonnance: "Type d'ordonnance",
  similaire: "Similaire (générique)",
  similaireAide: "Suivi dans le sous-registre des similaires.",
  alertePsychotrope: "Alerte de dispensation",
  alertePsychotropeAide: "Affichée au caissier dans la fenêtre ordonnancier au moment de la vente.",
  // ── prix & TVA
  prixAchatDefaut: "Prix d'achat HT par défaut",
  prixAchatDefautAide: "Proposé sur une nouvelle ligne d'achat ; le prix de chaque lot reste celui de sa facture.",
  prixReference: "Prix de référence (dernier lot)",
  prixReferenceValeur: (ppa, lot) => (lot ? `${ppa} · lot ${lot}` : ppa),
  prixReferenceAucun: "Aucune facture validée pour ce produit.",
  tvaAide: (type) => `Taux proposé pour un produit « ${type} » ; modifiable.`,
  tvaChoix: (type) => `Aucun taux par défaut pour « ${type} » : choisissez-le.`,
  // ── stock & réapprovisionnement (D-18)
  qteMinAide: "Utilisée par « À commander » (Assistant de commande) et la page Réapprovisionnement.",
  seuilAlerte: "Seuil d'alerte (stock bas)",
  seuilAlerteAide: "Déclenche l'alerte « Stock bas ». Distinct de la QTE MIN.",
  suggestion: (v) => `Suggestion calculée : ${v}`,
  appliquer: "Appliquer",
  fournisseurHabituel: "Fournisseur habituel",
  fournisseurAucun: "Aucun",
  fournisseurAide: "Premier fournisseur proposé pour ce produit dans un bon de commande.",
  // ── avancé
  avance: "Avancé",
  avanceAide: "Aide à la décision, limites, entité de distribution, tags et blocage de la vente.",
  avanceOuvrir: "Afficher les réglages avancés",
  avanceFermer: "Masquer les réglages avancés",
  criticite: "Criticité",
  criticiteAucune: "Non renseignée",
  criticiteAide: "Avec la valeur du produit, déclenche le calcul de la suggestion de QTE MIN.",
  importance: "Importance",
  classeRentabilite: "Classe de rentabilité",
  rentabiliteAuto: "Calculée automatiquement chaque nuit",
  rentabiliteAutoAide: "Décochez pour fixer la classe vous-même.",
  coefficientDemande: "Coefficient de demande (μ)",
  coefficientAide: (min, max) => `Remplace le coefficient de l'importance pour ce produit (entre ${min} exclu et ${max}).`,
  coefficientMotif: "Motif",
  coefficientEnregistrer: "Enregistrer le coefficient",
  coefficientRetirer: "Retirer le coefficient",
  coefficientApresCreation: "Disponible après la création du produit.",
  coefficientEnregistre: "Coefficient de demande enregistré.",
  coefficientRetire: "Coefficient de demande retiré.",
  coefficientInvalide: (max) => `Saisissez un coefficient supérieur à 0 et au plus ${max}.`,
  coefficientActuel: (mu) => `Actuel : ${mu}`,
  coefficientAucun: "Coefficient de l'importance",
  stockMaximal: "Stock maximal",
  quantiteMaximale: "Quantité maximale",
  quantiteMaximaleAide: "Cible de réapprovisionnement (facultatif).",
  entiteDistribution: "Entité de distribution",
  tags: "Tags",
  tagsAide: "Étiquettes de couleur pour classer et filtrer ce produit.",
  tagsAucun: "Aucun tag défini — créez-en depuis « Gérer les tags » de la liste des produits.",
  tagsEchec: "Le produit est enregistré, mais ses tags n'ont pas pu l'être : rouvrez la fiche pour les poser.",
  tagActif: (nom) => `Tag ${nom} (sélectionné)`,
  tagInactif: (nom) => `Tag ${nom}`,
  blocageVente: "Bloquer la vente libre",
  blocageVenteAide: "Le comptoir refuse ce produit ; CHIFA et instances restent possibles.",
  blocageMotif: "Motif du blocage",
  blocageMotifAide: "Affiché au caissier comme justification.",
  BLOCAGE_MOTIF_REQUIS: "Indiquez le motif du blocage de la vente libre.",
  // ── dermo
  dermoAide: "Placez ce produit dans les rayons-conseil et réglez son rang pour l'assistant au comptoir.",
  // ── validation (in addition to S2's M-S2-*)
  NUM_ENR_REQUIS: "Saisissez le N° d'enregistrement CNAS d'un médicament remboursable.",
  ENTIER_POSITIF: "Saisissez un nombre entier positif ou nul.",
  ENTIER_MAX: (max) => `${max} au maximum.`,
  MONTANT_INVALIDE: (max) => `Saisissez un montant entre 0,00 et ${max} DA.`,
  erreursTitre: (n) => (n > 1 ? `${n} champs sont à corriger :` : "Un champ est à corriger :"),
  // ── save / delete / leave
  enregistrer: "Enregistrer",
  creer: "Créer le produit",
  annuler: "Annuler",
  enregistre: "Produit enregistré.",
  cree: (nom) => `Produit « ${nom} » créé.`,
  rienAEnregistrer: "Aucune modification à enregistrer.",
  modifieAilleursTitre: "Fiche modifiée sur un autre poste",
  modifieAilleurs:
    "Ce produit a été enregistré par quelqu'un d'autre depuis l'ouverture de la fiche. Vos modifications ne sont pas enregistrées : rechargez la fiche pour partir de la version enregistrée.",
  recharger: "Recharger la fiche",
  echecEnregistrement: (raison) => `L'enregistrement n'a pas abouti : ${raison}`,
  supprimer: "Supprimer le produit",
  supprimerTitre: (nom) => `Supprimer « ${nom} » ?`,
  supprimerMessage: "Le produit sera retiré du catalogue. L'historique des ventes et des stocks reste conservé.",
  supprimerConfirmer: "Supprimer",
  supprime: (nom) => `Produit « ${nom} » supprimé.`,
  echecSuppression: (raison) => `Suppression impossible : ${raison}`,
  quitterTitre: "Quitter sans enregistrer ?",
  quitterMessage: "Les modifications de la fiche seront perdues.",
  quitter: "Quitter sans enregistrer",
  rester: "Continuer la saisie",
  navigationBloquee: "Enregistrez (Ctrl+S) ou annulez (Échap) la fiche avant de changer de page.",
  typeVerrouille: "Le type ne peut plus changer : des lots de ce produit existent.",
  // refonte WP-68 (A-77) — with lots, the base is fixed; another type of the same base stays possible
  typeVerrouilleBase: (base) => `Des lots de ce produit existent : seuls les types de base « ${base} » restent proposés.`,
});
